/**
 * Page d'administration des logs - Version simplifiée affichage brut
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { logService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { notify } from '../components/NotificationContainer';
import logger from '../utils/logger';

const LogsAdmin = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('parents');
  const [logs, setLogs] = useState([]);
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc' ou 'desc'
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!user || user.role !== 'ADMIN') {
      navigate('/');
      return;
    }
    loadLogs();
  }, [user, navigate, activeTab]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      const response = activeTab === 'parents'
        ? await logService.getLogsParents({ page_size: 2000 })
        : await logService.getLogsAdmins({ page_size: 2000 });

      const data = response.data.results || response.data;
      setLogs(Array.isArray(data) ? data : []);
    } catch (error) {
      logger.error('Erreur lors du chargement des logs:', error);
      notify.error('Erreur lors du chargement des logs');
    } finally {
      setLoading(false);
    }
  };

  // Filtrer et trier les logs par date et recherche
  const filteredLogs = logs.filter(log => {
    const logDate = new Date(log.timestamp);

    // Filtre par date de début
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      if (logDate < fromDate) return false;
    }

    // Filtre par date de fin
    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (logDate > toDate) return false;
    }

    // Filtre par recherche textuelle
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchId = log.id?.toString().includes(searchTerm);
      const matchAction = log.action?.toLowerCase().includes(searchLower);
      const matchUser = log.user_nom?.toLowerCase().includes(searchLower);
      const matchEmail = log.user_email?.toLowerCase().includes(searchLower);
      const matchDescription = log.description?.toLowerCase().includes(searchLower);
      const matchMessage = log.message_humain?.toLowerCase().includes(searchLower);
      const matchIp = log.ip_address?.includes(searchTerm);
      const matchUrl = log.url_path?.toLowerCase().includes(searchLower);

      if (!matchId && !matchAction && !matchUser && !matchEmail &&
          !matchDescription && !matchMessage && !matchIp && !matchUrl) {
        return false;
      }
    }

    return true;
  });

  const sortedLogs = [...filteredLogs].sort((a, b) => {
    const dateA = new Date(a.timestamp);
    const dateB = new Date(b.timestamp);
    return sortOrder === 'desc' ? dateB - dateA : dateA - dateB;
  });

  // Fonction pour télécharger les logs
  const downloadLogs = () => {
    const rawText = generateRawText();
    const blob = new Blob([rawText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `logs_${activeTab}_${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    notify.success('Logs téléchargés !');
  };

  // Générer le texte brut
  const generateRawText = () => {
    let rawText = '';

    // En-tête
    rawText += `===============================================================================\n`;
    rawText += `LOGS ${activeTab === 'parents' ? 'PARENTS' : 'ADMINISTRATEURS'}\n`;
    rawText += `Date d'export: ${new Date().toLocaleString('fr-FR')}\n`;
    rawText += `Total d'entrées: ${sortedLogs.length}\n`;
    if (dateFrom || dateTo) {
      rawText += `Période: ${dateFrom ? new Date(dateFrom).toLocaleDateString('fr-FR') : 'début'} - ${dateTo ? new Date(dateTo).toLocaleDateString('fr-FR') : 'aujourd\'hui'}\n`;
    }
    if (searchTerm) {
      rawText += `Recherche: "${searchTerm}"\n`;
    }
    rawText += `Ordre de tri: ${sortOrder === 'desc' ? 'Plus récent au plus ancien' : 'Plus ancien au plus récent'}\n`;
    rawText += `===============================================================================\n\n`;

    // Logs au format brut
    sortedLogs.forEach((log, index) => {
      const timestamp = new Date(log.timestamp).toLocaleString('fr-FR');
      const level = log.erreur ? 'ERROR' : log.impact_critique ? 'CRITICAL' : 'INFO';

      rawText += `[${index + 1}/${sortedLogs.length}] ───────────────────────────────────────────────────────\n`;
      rawText += `ID: #${log.id}\n`;
      rawText += `Date/Heure: ${timestamp}\n`;
      rawText += `Niveau: ${level}\n`;
      rawText += `Action: ${log.action}\n`;

      // Utilisateur
      if (log.user_nom) {
        rawText += `Utilisateur: ${log.user_nom}`;
        if (log.user_email) rawText += ` (${log.user_email})`;
        rawText += `\n`;
      } else {
        rawText += `Utilisateur: Non authentifié\n`;
      }

      // Informations réseau
      if (log.ip_address) rawText += `IP: ${log.ip_address}\n`;
      if (log.user_agent) rawText += `User-Agent: ${log.user_agent}\n`;

      // Informations HTTP
      if (log.methode_http) rawText += `Méthode HTTP: ${log.methode_http}\n`;
      if (log.url_path) rawText += `URL: ${log.url_path}\n`;
      if (log.status_code) rawText += `Code HTTP: ${log.status_code}\n`;
      if (log.duree_ms) rawText += `Durée: ${log.duree_ms}ms\n`;

      // Description
      rawText += `\nDescription:\n${log.description}\n`;

      // Message lisible
      if (log.message_humain) {
        rawText += `\nMessage:\n${log.message_humain}\n`;
      }

      // Données avant modification
      if (log.donnees_avant) {
        rawText += `\nDonnées AVANT:\n`;
        rawText += JSON.stringify(log.donnees_avant, null, 2) + '\n';
      }

      // Données après modification
      if (log.donnees_apres) {
        rawText += `\nDonnées APRÈS:\n`;
        rawText += JSON.stringify(log.donnees_apres, null, 2) + '\n';
      }

      // Erreur
      if (log.erreur && log.message_erreur) {
        rawText += `\nERREUR:\n${log.message_erreur}\n`;
      }

      // Remarques
      if (log.remarques) {
        rawText += `\nRemarques: ${log.remarques}\n`;
      }

      // Impact critique
      if (log.impact_critique) {
        rawText += `\nACTION CRITIQUE\n`;
      }

      rawText += `\n`;
    });

    if (sortedLogs.length === 0) {
      rawText += `Aucune entrée trouvée.\n`;
    }

    rawText += `\n===============================================================================\n`;
    rawText += `Fin du fichier de logs\n`;
    rawText += `===============================================================================\n`;

    return rawText;
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center text-xl">Chargement des logs...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* En-tête */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h1 className="text-2xl font-bold mb-4">Logs d'Administration</h1>

        {/* Onglets */}
        <div className="flex gap-3 mb-4">
          <button
            onClick={() => setActiveTab('parents')}
            className={`px-4 py-2 rounded font-medium ${
              activeTab === 'parents'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700'
            }`}
          >
            Actions Parents
          </button>
          <button
            onClick={() => setActiveTab('admins')}
            className={`px-4 py-2 rounded font-medium ${
              activeTab === 'admins'
                ? 'bg-purple-600 text-white'
                : 'bg-gray-200 text-gray-700'
            }`}
          >
            Actions Admins
          </button>
        </div>

        {/* Champ de recherche */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Recherche
          </label>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher par ID, action, utilisateur, email, description, IP, URL..."
            className="w-full px-3 py-2 border border-gray-300 rounded"
          />
        </div>

        {/* Filtres de date */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date de début
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date de fin
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Ordre de tri
            </label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded"
            >
              <option value="desc">Plus récent d'abord</option>
              <option value="asc">Plus ancien d'abord</option>
            </select>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 mb-4">
          <button
            onClick={loadLogs}
            className="px-4 py-2 bg-green-600 text-white rounded font-medium hover:bg-green-700"
          >
            Actualiser
          </button>
          <button
            onClick={downloadLogs}
            className="px-4 py-2 bg-blue-600 text-white rounded font-medium hover:bg-blue-700"
          >
            Télécharger
          </button>
          {(dateFrom || dateTo || searchTerm) && (
            <button
              onClick={() => {
                setDateFrom('');
                setDateTo('');
                setSearchTerm('');
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded font-medium hover:bg-gray-300"
            >
              Réinitialiser les filtres
            </button>
          )}
        </div>

        <p className="text-sm text-gray-600">
          {sortedLogs.length} entrée(s) sur {logs.length} au total
        </p>
      </div>

      {/* Affichage des logs */}
      <div className="bg-white rounded-lg shadow">
        <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">
            logs_{activeTab}_{new Date().toISOString().split('T')[0]}.txt
          </span>
          <button
            onClick={downloadLogs}
            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium"
          >
            Télécharger
          </button>
        </div>
        <div className="p-6 overflow-auto bg-white border border-gray-200" style={{maxHeight: '70vh'}}>
          <pre className="text-gray-800 font-mono text-sm leading-relaxed whitespace-pre-wrap"
               style={{fontFamily: "Consolas, Monaco, 'Courier New', monospace"}}>
{generateRawText()}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default LogsAdmin;
